import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  DatePicker,
  InputNumber,
  Tag,
  Descriptions,
  Tooltip,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SendOutlined, EyeOutlined } from "@ant-design/icons";
import { vatReportApi } from "../../api/vatApi";
import { IVatReport, VatReportType } from "../../types/vatReport.type";
import { formatNumber } from "../../utils/formatNumber";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;

const VatReportsPage: React.FC = () => {
  const { exchangeRate } = useExchangeRate();
  const [reports, setReports] = useState<IVatReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editingReport, setEditingReport] = useState<IVatReport | null>(null);
  const [selectedReport, setSelectedReport] = useState<IVatReport | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (searchText) {
        params.search = searchText;
      }
      const response = await vatReportApi.getAll(params);
      setReports(response.data);
    } catch (error) {
      message.error("Ошибка при загрузке отчетов НДС");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [searchText]);

  const handleCreate = () => {
    setEditingReport(null);
    form.resetFields();
    form.setFieldsValue({
      exchangeRate: exchangeRate,
    });
    setModalVisible(true);
  };

  const handleEdit = (report: IVatReport) => {
    setEditingReport(report);
    form.setFieldsValue({
      ...report,
      period: {
        startDate: dayjs(report.period.startDate),
        endDate: dayjs(report.period.endDate),
      },
    });
    setModalVisible(true);
  };

  const handleView = (report: IVatReport) => {
    setSelectedReport(report);
    setViewModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(id);
    try {
      await vatReportApi.delete(id);
      message.success("Отчет НДС успешно удален");
      fetchReports();
    } catch (error) {
      message.error("Ошибка при удалении отчета НДС");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleSubmit = async (id: string) => {
    setSubmitLoading(true);
    try {
      await vatReportApi.submit(id);
      message.success("Отчет НДС успешно отправлен");
      fetchReports();
    } catch (error) {
      message.error("Ошибка при отправке отчета НДС");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleFormSubmit = async (values: any) => {
    setSubmitLoading(true);
    try {
      const reportData = {
        ...values,
        period: {
          startDate: values.period.startDate.format("YYYY-MM-DD"),
          endDate: values.period.endDate.format("YYYY-MM-DD"),
        },
        exchangeRate: exchangeRate,
      };

      if (editingReport) {
        await vatReportApi.update(editingReport._id, reportData);
        message.success("Отчет НДС успешно обновлен");
      } else {
        await vatReportApi.create(reportData);
        message.success("Отчет НДС успешно создан");
      }
      setModalVisible(false);
      fetchReports();
    } catch (error) {
      message.error("Ошибка при сохранении отчета НДС");
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = [
    {
      title: "Номер отчета",
      dataIndex: "reportNumber",
      key: "reportNumber",
    },
    {
      title: "Тип отчета",
      dataIndex: "reportType",
      key: "reportType",
      render: (value: VatReportType) => (
        <Tag color="blue">{value}</Tag>
      ),
      filters: Object.values(VatReportType).map(type => ({ text: type, value: type })),
      onFilter: (value: any, record: IVatReport) => record.reportType === value,
    },
    {
      title: "Период",
      key: "period",
      render: (record: IVatReport) => (
        <div>
          {record.period.startDate} - {record.period.endDate}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="НДС с продаж - что мы должны государству">
          Наш долг государству
        </Tooltip>
      ),
      dataIndex: "vatLiability",
      key: "vatLiability",
      render: (value: { USD: number; TJS: number }) => (
        <div>
          <div>{formatNumber(value.TJS)} TJS</div>
          <small>{formatNumber(value.USD)} USD</small>
        </div>
      ),
      sorter: (a: IVatReport, b: IVatReport) => a.vatLiability.TJS - b.vatLiability.TJS,
    },
    {
      title: (
        <Tooltip title="НДС с покупок - что нам должны клиенты">
          Долг клиентов нам
        </Tooltip>
      ),
      dataIndex: "vatCredit",
      key: "vatCredit",
      render: (value: { USD: number; TJS: number }) => (
        <div>
          <div>{formatNumber(value.TJS)} TJS</div>
          <small>{formatNumber(value.USD)} USD</small>
        </div>
      ),
      sorter: (a: IVatReport, b: IVatReport) => a.vatCredit.TJS - b.vatCredit.TJS,
    },
    {
      title: "Чистый баланс",
      dataIndex: "netVatLiability",
      key: "netVatLiability",
      render: (value: { USD: number; TJS: number }) => (
        <div style={{ 
          color: value.TJS >= 0 ? '#52c41a' : '#ff4d4f',
          fontWeight: 'bold'
        }}>
          <div>{formatNumber(value.TJS)} TJS</div>
          <small>{formatNumber(value.USD)} USD</small>
          <div style={{ fontSize: '10px', marginTop: '2px' }}>
            {value.TJS >= 0 ? 'Нам должны' : 'Мы должны'}
          </div>
        </div>
      ),
      sorter: (a: IVatReport, b: IVatReport) => a.netVatLiability.TJS - b.netVatLiability.TJS,
    },
    {
      title: "Статус",
      dataIndex: "isSubmitted",
      key: "isSubmitted",
      render: (value: boolean) => (
        <Tag color={value ? "green" : "orange"}>
          {value ? "Отправлен" : "Черновик"}
        </Tag>
      ),
      filters: [
        { text: 'Отправлен', value: true },
        { text: 'Черновик', value: false },
      ],
      onFilter: (value: any, record: IVatReport) => record.isSubmitted === value,
    },
    {
      title: "Действия",
      key: "actions",
      render: (record: IVatReport) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            Просмотр
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Редактировать
          </Button>
          {!record.isSubmitted && (
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => handleSubmit(record._id)}
              loading={submitLoading}
            >
              Отправить
            </Button>
          )}
          <Popconfirm
            title="Вы уверены, что хотите удалить этот отчет?"
            onConfirm={() => handleDelete(record._id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deleteLoading === record._id}
            >
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "16px" }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Поиск по номеру отчета"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: "400px" }}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Создать отчет
            </Button>
          </Col>
        </Row>
      </div>

      <Table
        columns={columns}
        dataSource={reports}
        rowKey="_id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} из ${total} отчетов`,
        }}
      />

      <Modal
        title={editingReport ? "Редактировать отчет НДС" : "Создать отчет НДС"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16, fontSize: '12px', color: '#666' }}>
          Курс обмена будет взят автоматически из общих настроек системы
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reportType"
                label="Тип отчета"
                rules={[{ required: true, message: "Выберите тип отчета" }]}
              >
                <Select>
                  {Object.values(VatReportType).map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={["period", "startDate"]}
                label="Дата начала"
                rules={[{ required: true, message: "Выберите дату начала" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["period", "endDate"]}
                label="Дата окончания"
                rules={[{ required: true, message: "Выберите дату окончания" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Примечания"
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingReport ? "Обновить" : "Создать"}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Просмотр отчета НДС"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedReport && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Номер отчета" span={2}>
              {selectedReport.reportNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Тип отчета">
              {selectedReport.reportType}
            </Descriptions.Item>
            <Descriptions.Item label="Период">
              {selectedReport.period.startDate} - {selectedReport.period.endDate}
            </Descriptions.Item>
            <Descriptions.Item label="Курс обмена">
              {selectedReport.exchangeRate} TJS/USD
            </Descriptions.Item>
            <Descriptions.Item label="Статус">
              <Tag color={selectedReport.isSubmitted ? "green" : "orange"}>
                {selectedReport.isSubmitted ? "Отправлен" : "Черновик"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Продажи (без НДС)" span={2}>
              <div>{formatNumber(selectedReport.totalSales.amountWithoutVat.TJS)} TJS</div>
              <small>{formatNumber(selectedReport.totalSales.amountWithoutVat.USD)} USD</small>
            </Descriptions.Item>
            <Descriptions.Item label="Продажи (НДС)" span={2}>
              <div>{formatNumber(selectedReport.totalSales.vatAmount.TJS)} TJS</div>
              <small>{formatNumber(selectedReport.totalSales.vatAmount.USD)} USD</small>
            </Descriptions.Item>
            <Descriptions.Item label="Продажи (всего)" span={2}>
              <div>{formatNumber(selectedReport.totalSales.totalAmount.TJS)} TJS</div>
              <small>{formatNumber(selectedReport.totalSales.totalAmount.USD)} USD</small>
            </Descriptions.Item>
            <Descriptions.Item label="Покупки (без НДС)" span={2}>
              <div>{formatNumber(selectedReport.totalPurchases.amountWithoutVat.TJS)} TJS</div>
              <small>{formatNumber(selectedReport.totalPurchases.amountWithoutVat.USD)} USD</small>
            </Descriptions.Item>
            <Descriptions.Item label="Покупки (НДС)" span={2}>
              <div>{formatNumber(selectedReport.totalPurchases.vatAmount.TJS)} TJS</div>
              <small>{formatNumber(selectedReport.totalPurchases.vatAmount.USD)} USD</small>
            </Descriptions.Item>
            <Descriptions.Item label="Покупки (всего)" span={2}>
              <div>{formatNumber(selectedReport.totalPurchases.totalAmount.TJS)} TJS</div>
              <small>{formatNumber(selectedReport.totalPurchases.totalAmount.USD)} USD</small>
            </Descriptions.Item>
            <Descriptions.Item label="Наш долг государству" span={2}>
              <div style={{ color: '#ff4d4f' }}>
                {formatNumber(selectedReport.vatLiability.TJS)} TJS
              </div>
              <small style={{ color: '#ff4d4f' }}>
                {formatNumber(selectedReport.vatLiability.USD)} USD
              </small>
            </Descriptions.Item>
            <Descriptions.Item label="Долг клиентов нам" span={2}>
              <div style={{ color: '#52c41a' }}>
                {formatNumber(selectedReport.vatCredit.TJS)} TJS
              </div>
              <small style={{ color: '#52c41a' }}>
                {formatNumber(selectedReport.vatCredit.USD)} USD
              </small>
            </Descriptions.Item>
            <Descriptions.Item label="Чистый баланс" span={2}>
              <div style={{ 
                color: selectedReport.netVatLiability.TJS >= 0 ? '#52c41a' : '#ff4d4f',
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                {formatNumber(selectedReport.netVatLiability.TJS)} TJS
              </div>
              <small style={{ 
                color: selectedReport.netVatLiability.USD >= 0 ? '#52c41a' : '#ff4d4f',
                fontWeight: 'bold'
              }}>
                {formatNumber(selectedReport.netVatLiability.USD)} USD
              </small>
              <div style={{ 
                fontSize: '12px', 
                color: selectedReport.netVatLiability.TJS >= 0 ? '#52c41a' : '#ff4d4f',
                marginTop: '4px'
              }}>
                {selectedReport.netVatLiability.TJS >= 0 ? 'Нам должны' : 'Мы должны'}
              </div>
            </Descriptions.Item>
            {selectedReport.notes && (
              <Descriptions.Item label="Примечания" span={2}>
                {selectedReport.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default VatReportsPage; 